// scenarioBadges — shared vocabulary for scenario TYPE chips and run VERDICT chips.
//
// Scenario TYPE ("what kind of scenario is this?") is informational metadata and
// must never be rendered in verdict colors. Verdict ("what happened when it ran?")
// comes from actual run results. Keeping both palettes here prevents the two
// vocabularies from being conflated again.

// ---------------------------------------------------------------------------
// Scenario type (path classification) — neutral, informational palette
// ---------------------------------------------------------------------------

export type ScenarioPathType = 'happy' | 'failure' | 'negative' | 'timeout' | 'alt';

export interface ChipStyle {
  label: string;
  /** Foreground color. */
  color: string;
  /** Background color. */
  bg: string;
}

/**
 * Neutral (slate/blue/violet) palette — deliberately avoids green/amber/red so
 * a type chip can never be mistaken for a pass/fail verdict.
 */
export const TYPE_CHIP_STYLES: Record<ScenarioPathType, ChipStyle> = {
  happy: { label: 'Happy path', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.14)' },
  failure: { label: 'Failure path', color: '#818cf8', bg: 'rgba(129, 140, 248, 0.14)' },
  negative: { label: 'Negative test', color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.14)' },
  timeout: { label: 'Timeout test', color: '#22d3ee', bg: 'rgba(34, 211, 238, 0.14)' },
  alt: { label: 'Alt path', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.14)' },
};

/**
 * v2 ledger vocabulary — one GLYPH per kind (shape carries the kind; color
 * carries the verdict, never the other way around). The glyphs and the
 * one-line definitions are the in-product curriculum for §3 of the brief.
 */
export const KIND_GLYPHS: Record<ScenarioPathType, string> = {
  happy: '○',
  failure: '◆',
  negative: '⊘',
  timeout: '◔',
  alt: '◇',
};

export const KIND_DEFINITIONS: Record<ScenarioPathType, string> = {
  happy: 'the intended journey, end to end — passing means the design completes.',
  failure: 'a designed unhappy route — passing means the design fails exactly where declared.',
  negative: 'deliberately violates a precondition — passing means the design refuses correctly.',
  timeout: 'a saga clock elapses — passing means the design stops where the timeout says.',
  alt: 'an alternate declared route through the flow.',
};

/** Uppercase display name for a kind (ledger group headers, chips). */
export const KIND_NAMES: Record<ScenarioPathType, string> = {
  happy: 'HAPPY',
  failure: 'FAILURE',
  negative: 'NEGATIVE',
  timeout: 'TIMEOUT',
  alt: 'ALT',
};

/**
 * Verdict → color for ledger glyphs. `null` (never ran) renders dim — a run
 * that has not happened must not read as any verdict.
 */
export const VERDICT_GLYPH_COLORS: Record<Exclude<EffectiveVerdict, null>, string> = {
  pass: 'var(--green)',
  fail: 'var(--red)',
  error: '#fb923c',
  none: 'var(--text-muted)',
  skipped: 'var(--text-muted)',
};

/**
 * Classify a scenario label by EXACT PREFIX — never substring matching.
 * Moment's generated label vocabulary: 'Happy Path: …', 'Failure Path: …' /
 * 'Failure: …' (old format), 'Negative: …', 'Timeout: …' (upgraded saga
 * artifacts). Anything else — including labels like "Unhappy path" that a
 * substring check would mis-read as happy — is an alt path.
 */
export function classifyScenarioLabel(label: string): ScenarioPathType {
  if (label.startsWith('Happy Path')) return 'happy';
  if (label.startsWith('Failure Path') || label.startsWith('Failure:')) return 'failure';
  if (label.startsWith('Negative')) return 'negative';
  if (label.startsWith('Timeout:')) return 'timeout';
  return 'alt';
}

/**
 * Classify a manifest entry's scenario TYPE.
 *
 * The walk-derived `kind` (upgraded Moment contract) is the authority when
 * present and recognized; explicit manifest flags win next; label
 * classification falls back to exact-prefix matching (classifyScenarioLabel).
 * A 'Happy Path' label without the explicit flag stays 'alt' — the flag is
 * the authority for happy. Unknown future `kind` values fall through to the
 * flag/label rules rather than being guessed at.
 */
export function classifyScenarioType(entry: {
  kind?: string;
  isHappyPath?: boolean;
  isNegative?: boolean;
  scenarioLabel?: string;
}): ScenarioPathType {
  switch (entry.kind) {
    case 'happy':
      return 'happy';
    case 'negative':
      return 'negative';
    case 'failure':
      return 'failure';
    case 'timeout':
      return 'timeout';
    case 'variant':
      return 'alt';
  }
  if (entry.isHappyPath) return 'happy';
  if (entry.isNegative) return 'negative';
  const byLabel = classifyScenarioLabel(entry.scenarioLabel ?? '');
  return byLabel === 'happy' ? 'alt' : byLabel;
}

// ---------------------------------------------------------------------------
// Run verdict — sourced from actual results, never from scenario type
// ---------------------------------------------------------------------------

/**
 * 'pass' | 'fail'  — assertions ran to completion
 * 'error'          — run aborted before completing (simulation status 'failed')
 * 'none'           — run completed but produced zero assertions (inconclusive)
 * 'skipped'        — scenario was skipped by a suite run
 * null             — no run result available
 */
export type EffectiveVerdict = 'pass' | 'fail' | 'error' | 'none' | 'skipped' | null;

export const VERDICT_CHIP_STYLES: Record<Exclude<EffectiveVerdict, null>, ChipStyle> = {
  pass: { label: 'PASS', color: 'var(--green)', bg: 'rgba(74, 222, 128, 0.14)' },
  fail: { label: 'FAIL', color: 'var(--red)', bg: 'rgba(239, 68, 68, 0.14)' },
  error: { label: 'ERROR', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.16)' },
  none: { label: '—', color: 'var(--text-muted)', bg: 'transparent' },
  skipped: { label: 'SKIPPED', color: 'var(--text-muted)', bg: 'rgba(148, 163, 184, 0.12)' },
};

/**
 * Derive the effective verdict for the active scenario from simulation status
 * plus assertion summary, honoring richer verdict values from the store when
 * they exist (the store may report 'error' / 'inconclusive' directly).
 */
export function deriveEffectiveVerdict(
  status: string,
  summary: { total: number; failed: number; skipped?: number },
  storeVerdict?: string | null,
): EffectiveVerdict {
  // Aborted runs are errors regardless of what assertions completed.
  if (status === 'failed') return 'error';
  if (status !== 'completed') return null;
  // Feature-detect richer store semantics without depending on them.
  if (storeVerdict === 'error') return 'error';
  if (storeVerdict === 'inconclusive') return 'none';
  // Zero evaluated assertions (none, or all skipped) is inconclusive.
  if (summary.total - (summary.skipped ?? 0) <= 0) return 'none';
  return summary.failed === 0 ? 'pass' : 'fail';
}

/**
 * Normalize a verdict string coming from suite results (which may grow new
 * values like 'error' or 'skipped') into an EffectiveVerdict.
 */
export function normalizeSuiteVerdict(verdict: string | null | undefined): EffectiveVerdict {
  switch (verdict) {
    case 'pass':
      return 'pass';
    case 'fail':
      return 'fail';
    case 'error':
      return 'error';
    case 'skipped':
      return 'skipped';
    case 'inconclusive':
    case 'none':
      return 'none';
    default:
      return null;
  }
}
